import * as React from 'react';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {HoverPopover} from 'shells/hover-popover/hover-popover';
import {
    DescriptionIcon,
    ArchiveIcon,
    EditThickerIcon,
    RetryRegularIcon,
    EyeIcon,
    EyeSlashIcon,
} from 'shells/icon';

import {CustomFieldsRowItemStat} from './custom-fields-row-item-stat';
import type {ListItemStat} from './types';

import styles from './custom-fields-row-item.module.css';

type Props = {
    displayName: string;
    fieldType: string;
    description: string | null | undefined;
    onEdit: () => void;
    onArchive: () => void;
    onRestore?: () => void;
    isViewingArchived: boolean;
    isOnPipelineTable: boolean;
    fieldIsShownInPipeline: () => boolean;
    onReorderField: () => void;
    stats: Array<ListItemStat>;
};

export function CustomFieldRowItem(props: Props) {
    const {
        isViewingArchived,
        isOnPipelineTable,
        fieldIsShownInPipeline,
        onReorderField,
        displayName,
        onRestore,
        onArchive,
        description,
        fieldType,
        onEdit,
    } = props;
    const descriptionIconRef = React.useRef(null);

    const actionMenuOptions = React.useMemo(() => {
        const options = [];

        if (isViewingArchived) {
            options.push({
                label: 'Restore',
                handleSelect: () => {
                    if (onRestore) {
                        onRestore();
                    }
                },
                icon: <RetryRegularIcon size={16} />,
            });
        }
        if (isOnPipelineTable) {
            options.push({
                label: fieldIsShownInPipeline() ? 'Hide from pipeline' : 'Show in pipeline',
                handleSelect: onReorderField,
                icon: fieldIsShownInPipeline() ? <EyeSlashIcon size={16} /> : <EyeIcon size={16} />,
            });
        }
        if (!isViewingArchived) {
            options.push({
                label: 'Edit field',
                handleSelect: () => {
                    onEdit();
                },
                icon: <EditThickerIcon size={16} />,
            });
            options.push({
                label: 'Archive',
                variant: 'destructive',
                handleSelect: () => {
                    onArchive();
                },
                icon: <ArchiveIcon size={16} />,
            });
        }

        return options;
    }, [
        isViewingArchived,
        isOnPipelineTable,
        onReorderField,
        onRestore,
        fieldIsShownInPipeline,
        onEdit,
        onArchive,
    ]);

    return (
        <div className={styles.row}>
            <div className={styles['left-section']}>
                <div className={styles['row-name']}>
                    {isViewingArchived ? (
                        <span>{displayName}</span>
                    ) : (
                        <span onClick={onEdit} className={styles['row-name-clickable']}>
                            {displayName}
                        </span>
                    )}
                    {description && (
                        <span ref={descriptionIconRef} className={styles['description-icon']}>
                            <DescriptionIcon size={12} />
                            {/* @ts-expect-error */}
                            <HoverPopover
                                anchorRef={descriptionIconRef}
                                location='right'
                                isDialog={true}
                                bodyColor='light'
                                customStyles={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                <div>{description}</div>
                            </HoverPopover>
                        </span>
                    )}
                </div>
                <span className={styles['row-type']}>{fieldType}</span>
            </div>
            <div className={styles['right-section']}>
                {!isViewingArchived &&
                    props.stats.map((stat, index) => (
                        // @ts-expect-error
                        <CustomFieldsRowItemStat key={stat.id} stat={stat} index={index} />
                    ))}
                {/* @ts-expect-error */}
                <ActionMenu options={actionMenuOptions} />
            </div>
        </div>
    );
}
